import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  items?: FAQItem[];
  question?: string;
  answer?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

/**
 * FAQ component for displaying frequently asked questions with expandable answers
 */
const FAQ: React.FC<FAQProps> = ({
  items = [], // Provide empty array as default
  question,
  answer,
  title = "Frequently Asked Questions",
  subtitle,
  className = ""
}) => {
  // If individual question/answer provided, create items array
  const faqItems = question && answer
    ? [{ question, answer }]
    : items;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Toggle FAQ item open/closed
  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={`w-full max-w-4xl mx-auto ${className}`}>
      {/* Title and subtitle */}
      {title && (
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">{title}</h2>
          {subtitle && (
            <p className="text-neutral-600 text-lg max-w-2xl mx-auto dark:text-neutral-400">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* FAQ items */}
      <div className="space-y-4">
        {faqItems.map((item, index) => (
          <div 
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white dark:bg-neutral-800 dark:border-neutral-700"
          >
            {/* Question (always visible) */}
            <button
              className="flex items-center justify-between w-full px-6 py-4 text-left focus:outline-none"
              onClick={() => toggleItem(index)}
              aria-expanded={openIndex === index}
              aria-controls={`faq-answer-${index}`}
            >
              <span className="font-medium text-lg">{item.question}</span>
              <svg
                className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
                  openIndex === index ? 'transform rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Answer (collapsible) */}
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  id={`faq-answer-${index}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-4 text-neutral-600 dark:text-neutral-300">
                    <div className="border-t border-gray-200 pt-4 dark:border-neutral-700">
                      {item.answer}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

// FAQList component that's used directly in pages
export const FAQList: React.FC<{ faqs?: Array<FAQItem> }> = ({ faqs = [] }) => {
  return <FAQ items={faqs} title="" />;
};

export default FAQ;